import { tmpdir } from 'node:os';
import { spawn, type ChildProcess } from 'node:child_process';
import { existsSync } from 'node:fs';
import { join } from 'node:path';
import { BaseAdapter } from './base/BaseAdapter';
import { ChatOptions, ChatResponse, StreamChunk, Tool } from '../types';
import { handleProviderError } from '../utils/errors';
import { contentToString } from '../utils/content-helpers';

// Lazy: referencing `__dirname` at module top-level crashes browser bundles (ai.libx.js index → AIClient
// eagerly imports this Node-only adapter). Computed only when actually spawning, which never happens in-browser.
export const helperInfo = (): { path: string; bin: string } => {
	// The helper MUST run under `node`, NEVER bun: this subprocess exists precisely because Bun's h2 client
	// can't negotiate Cursor's connectRPC backend — under bun the run starts (RUNNING) then dies with an
	// opaque status ERROR. node CANNOT strip TS types for files under node_modules (ERR_UNSUPPORTED_NODE_
	// MODULES_TYPE_STRIPPING), so we must run the COMPILED .js, never the .ts — even when the adapter itself
	// is loaded from src/ via bun's "bun" export condition (then __dirname is src/adapters/ and the .js lives
	// in ../../build/adapters/). Prefer a sibling .js (build/default export), then the build/ .js (src export);
	// fall back to the .ts only for an un-built dev checkout outside node_modules (where node CAN strip types).
	// CURSOR_NODE_BIN overrides the bin (tests use bun + a fake helper); CURSOR_NODE_HELPER overrides the script.
	const js = [
		join(__dirname, 'cursor-node-helper.js'),
		join(__dirname, '..', '..', 'build', 'adapters', 'cursor-node-helper.js'),
	].find(existsSync);
	return {
		path: process.env.CURSOR_NODE_HELPER || js || join(__dirname, 'cursor-node-helper.ts'),
		bin: process.env.CURSOR_NODE_BIN || 'node',
	};
};

function toModelSelection(model: string): { id: string; params?: Array<{ id: string; value: string }> } {
	if (model.endsWith('-fast')) return { id: model.replace(/-fast$/, ''), params: [{ id: 'fast', value: 'true' }] };
	return { id: model };
}

function mapEvent(event: any): StreamChunk[] {
	// Synthetic one-liners (task/status) carry COMPLETE text, so a single LEADING newline
	// isolates each on its own row. `thinking` is the exception: the SDK streams it as fragmented deltas,
	// so it must NOT be newline-prefixed per chunk (that shatters one sentence into a ragged column) —
	// the deltas concatenate into one flowing line, and onData() opens the run with one separating `\n`.
	// No trailing newline: matches agentx's open-reasoning-line contract (next non-reasoning output closes it).
	const reason = (s: string): StreamChunk[] => [{ content: '', reasoningContent: `\n${s}` }];
	switch (event?.type) {
		case 'assistant':
			return (event.message?.content ?? [])
				.filter((b: any) => b.type === 'text' && b.text)
				.map((b: any) => ({ content: b.text }));
		case 'thinking':
			return event.text ? [{ content: '', reasoningContent: event.text }] : [];
		case 'tool_call': {
			if (!event.name) return [];
			// Structured, display-only tool activity (Cursor executes its OWN tools — NEVER dispatched by the
			// caller). Hosts render it as tool cards; plain hosts fall back to a printable line. Surfacing
			// running + completed + error lets a card flip status; `completed` carries no card of its own if a
			// host chooses to ignore it. See StreamChunk.toolActivity.
			const status: 'running' | 'completed' | 'error' =
				event.status === 'error' ? 'error' : event.status === 'completed' ? 'completed' : 'running';
			// Cursor SDK's SDKToolUseMessage uses snake_case `call_id`, and carries the tool `result` +
			// `truncated` flags on the completed/error transition — forward them so hosts can render a body.
			return [{ content: '', toolActivity: { id: event.call_id ?? event.callId ?? event.id, name: event.name, input: event.args, output: event.result, status, truncated: event.truncated } }];
		}
		case 'task':
			return event.text ? reason(`⚙ ${event.text}`) : [];
		case 'status':
			return event.status === 'ERROR' ? reason('✗ cursor run error') : [];
		default:
			return [];
	}
}

function buildPrompt(messages: ChatOptions['messages']): string {
	// System messages must ride the prompt — cursor's agent API has no system role, and
	// silently dropping them strips the caller's entire instruction set (models then look
	// "non-compliant" when they simply never saw the instructions).
	const sys = messages.filter(m => m.role === 'system')
		.map(m => contentToString(m.content).trim()).filter(Boolean).join('\n\n');
	const turns = messages.filter(m => m.role === 'user' || m.role === 'assistant');
	const latest = turns.length ? contentToString(turns[turns.length - 1].content) : '';
	const prior = turns.slice(0, -1)
		.map(t => ({ role: t.role, text: contentToString(t.content).trim() }))
		.filter(t => t.text)
		.map(t => `${t.role === 'user' ? 'User' : 'Assistant'}: ${t.text}`);
	const body = prior.length
		? `Conversation so far:\n${prior.join('\n\n')}\n\n---\nNow respond to the latest user message:\n${latest}`
		: latest;
	return sys ? `${sys}\n\n---\n\n${body}` : body;
}

/** Cursor's backend occasionally returns a run with status ERROR that is a transient flake (an internal
 *  500/timeout/overload) rather than a real config/auth problem — retrying the same prompt succeeds. We
 *  match on the surfaced message: retry generic/internal/network blips; NEVER retry auth/quota/model/arg
 *  errors (retrying those just wastes turns and masks the real fix). Conservative: unknown → not transient.
 *  An OPAQUE terminal ERROR (status ERROR with no message — see cursor-node-helper) is treated as NON-transient:
 *  in practice it is a persistent model-routing failure (e.g. an explicit model that isn't runnable over the
 *  API-key/headless path while "auto" is), and retrying it just triples the wall-clock on a doomed run. */
const TRANSIENT_CURSOR_ERROR = /internal|timeout|timed out|temporar|unavailable|overload|rate.?limit|too many|503|502|500|429|econn|socket|network|stream|aborted by the server|please try again/i;
const PERMANENT_CURSOR_ERROR = /unauthor|forbidden|invalid api key|api key|authentication|permission denied|quota|billing|payment|not found|unknown model|invalid model|invalid (argument|request|param)|bad request|status ERROR|not runnable|400|401|403|404/i;
export function isTransientCursorError(message: string | undefined): boolean {
	const m = message ?? '';
	if (PERMANENT_CURSOR_ERROR.test(m)) return false; // permanent always wins (e.g. "invalid api key: internal id")
	return TRANSIENT_CURSOR_ERROR.test(m);
}

/** Bounded retry on a transient cursor backend ERROR (read at call-time so env overrides apply; tests/tuning). */
const cursorErrorRetries = (): number => { const n = Number(process.env.CURSOR_ERROR_RETRIES); return Number.isFinite(n) ? n : 2; };
const cursorErrorRetryBaseMs = (): number => { const n = Number(process.env.CURSOR_ERROR_RETRY_BASE_MS); return Number.isFinite(n) && n >= 0 ? n : 500; };
const log = { warn: (...a: unknown[]) => console.warn(...a) };

/** Latest user turn only — used for warm-session follow-ups (the Cursor agent retains prior context). */
function latestUserMessage(messages: ChatOptions['messages']): string {
	for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === 'user') return contentToString(messages[i].content);
	return buildPrompt(messages);
}

/** Convert ai.libx Tool[] to serializable definitions for the helper subprocess. */
function toToolDefs(tools?: Tool[]): Array<{ name: string; description?: string; inputSchema: object }> | undefined {
	if (!tools?.length) return undefined;
	return tools.map(t => ({
		name: t.function.name,
		description: t.function.description,
		inputSchema: t.function.parameters,
	}));
}

/** ToolExecutor callback type — provided by the host (e.g. agent.libx.js) via providerOptions. */
export type CursorToolExecutor = (name: string, args: Record<string, any>) => Promise<string>;

/**
 * Spawn the helper with an IPC channel and wire up tool execution relay.
 * When the helper's customTool execute is called, it sends {t:'tool_exec', callId, name, args} via IPC.
 * We call toolExecutor and reply with {t:'tool_result', callId, result}.
 */
function spawnHelper(toolExecutor?: CursorToolExecutor, detachTty = false): ChildProcess {
	const hasIpc = !!toolExecutor;
	// `detachTty`: a long-lived (warm) helper must NOT inherit the parent's stderr — if that fd is the
	// controlling TTY, the Cursor SDK's local executor can tcsetattr it and clobber the host's raw-mode
	// termios (symptom: a wedged REPL echoing ^C/^[ literally). 'pipe' captures stderr for diagnostics
	// without handing the child a TTY fd.
	const errFd = detachTty ? 'pipe' : 'inherit';
	const { path: hPath, bin: hBin } = helperInfo();
	const child = spawn(hBin, [hPath], {
		stdio: hasIpc ? ['pipe', 'pipe', errFd, 'ipc'] : ['pipe', 'pipe', errFd],
		env: process.env,
	});
	if (hasIpc) {
		child.on('message', async (msg: any) => {
			if (msg?.t !== 'tool_exec') return;
			try {
				const result = await toolExecutor!(msg.name, msg.args ?? {});
				child.send({ t: 'tool_result', callId: msg.callId, result });
			} catch (e: any) {
				child.send({ t: 'tool_result', callId: msg.callId, result: `Error: ${e?.message ?? e}` });
			}
		});
	}
	return child;
}

/** Minimal single-consumer async channel: the warm session pushes stream chunks, send() iterates them. */
class Chan<T> {
	private items: T[] = [];
	private wake?: () => void;
	private ended = false;
	private err?: Error;
	push(v: T): void { this.items.push(v); this.wake?.(); }
	end(err?: Error): void { this.ended = true; this.err = err; this.wake?.(); }
	async *iterate(): AsyncIterable<T> {
		for (;;) {
			while (this.items.length) yield this.items.shift()!;
			if (this.ended) { if (this.err) throw this.err; return; }
			await new Promise<void>(r => { this.wake = r; });
			this.wake = undefined;
		}
	}
}

/**
 * A long-lived helper subprocess holding ONE Cursor agent, reused across turns. Turns are sequential
 * (the host awaits each fully before the next), so at most one prompt is "active": the persistent
 * stdout reader routes that prompt's events into its Chan. On abort we KILL the session (the connect-node
 * h2 stream can corrupt on mid-run cancel) — the next turn transparently recreates it.
 */
export class CursorSession {
	private child: ChildProcess;
	private buf = '';
	private active?: Chan<StreamChunk>;
	private firstSent = false;
	/** True when the last emitted event was a `thinking` delta. Used to concatenate the SDK's fragmented
	 *  thinking stream into one flowing line, prepending a single `\n` only when a thinking run STARTS. */
	private lastWasThinking = false;
	dead = false;
	/** Error from a {t:'error'} that arrived before any send() — e.g. Agent.create failure during startup.
	 *  Thrown by the first send() so the real message reaches the caller instead of the generic "cursor helper exited". */
	private startupError?: Error;
	/** Resolves when the current turn's run has fully settled (helper sent {t:'done'}/{t:'error'} or the
	 *  session died). The next send() awaits this so a cancelled turn's late {t:'done'} is never
	 *  cross-attributed to the following turn. */
	private settled: Promise<void> = Promise.resolve();
	private resolveSettled?: () => void;

	private stderrBuf = '';

	constructor(private init: Record<string, any>, toolExecutor?: CursorToolExecutor) {
		this.child = spawnHelper(toolExecutor, /* detachTty */ true);
		this.child.on('error', e => this.die(e as Error));
		// Defer die() so any buffered stdout {t:'error'} is processed first (setImmediate fires after I/O callbacks).
		this.child.on('exit', () => setImmediate(() => {
			const hint = this.stderrBuf.trim().slice(-500); // last 500 chars of stderr
			this.die(new Error(hint ? `cursor helper exited — ${hint}` : 'cursor helper exited'));
		}));
		this.child.stdout!.on('data', (d: Buffer) => this.onData(d));
		// Keep only the tail — a long-lived warm session must not grow this unboundedly.
		this.child.stderr!.on('data', (d: Buffer) => { this.stderrBuf = (this.stderrBuf + d.toString()).slice(-2000); });
		this.child.stdin!.write(JSON.stringify({ ...init, prompt: undefined, persistent: true, stream: true }) + '\n');
	}

	private endTurn(): void { this.active = undefined; this.resolveSettled?.(); this.resolveSettled = undefined; }

	private onData(d: Buffer): void {
		this.buf += d.toString();
		let nl: number;
		while ((nl = this.buf.indexOf('\n')) >= 0) {
			const line = this.buf.slice(0, nl).trim();
			this.buf = this.buf.slice(nl + 1);
			if (!line) continue;
			let msg: any;
			try { msg = JSON.parse(line); } catch { continue; }
			const a = this.active;
			if (msg.t === 'event') {
				if (a) {
					const chunks = mapEvent(msg.e);
					if (chunks.length) {
						const isThinking = msg.e?.type === 'thinking';
						// open a thinking RUN with one separating newline; subsequent deltas flow into it
						if (isThinking && !this.lastWasThinking) chunks[0].reasoningContent = '\n' + (chunks[0].reasoningContent ?? '');
						for (const c of chunks) a.push(c);
						this.lastWasThinking = isThinking;
					}
				}
			}
			else if (msg.t === 'done') { if (a) { a.push({ content: '', finishReason: 'stop' }); a.end(); } this.endTurn(); }
			else if (msg.t === 'error') { if (a) { a.end(new Error(msg.message)); this.endTurn(); } else this.startupError = new Error(msg.message); }
			// {t:'ready'} needs no handling — prompts queue on the helper's stdin until the agent is up.
		}
	}

	private die(e: Error): void {
		if (this.dead) return;
		this.dead = true;
		this.active?.end(e);
		this.endTurn();
	}

	kill(): void { this.dead = true; try { this.child.kill('SIGTERM'); } catch { /* already gone */ } }

	async *send(messages: ChatOptions['messages'], signal?: AbortSignal): AsyncIterable<StreamChunk> {
		if (this.startupError) throw this.startupError;
		if (this.dead) throw new Error('cursor session is dead');
		const prompt = this.firstSent ? latestUserMessage(messages) : buildPrompt(messages);
		this.firstSent = true;
		// Bounded retry on a TRANSIENT backend ERROR (a flaky run-start/run-status that succeeds on
		// re-issue). Only retry when NOTHING was streamed yet (can't replay a half-emitted turn — that
		// would duplicate output) and the turn wasn't aborted. Permanent errors (auth/quota/model) and
		// mid-stream errors propagate immediately, preserving the existing fail-fast + cancel semantics.
		const maxRetries = cursorErrorRetries();
		for (let attempt = 0; ; attempt++) {
			let emitted = false;
			try {
				for await (const c of this.runAttempt(prompt, signal)) { emitted = true; yield c; }
				return;
			} catch (e: any) {
				if (emitted || signal?.aborted || this.dead || attempt >= maxRetries || !isTransientCursorError(e?.message)) throw e;
				const delay = cursorErrorRetryBaseMs() * 2 ** attempt;
				log.warn(`cursor: transient run error "${e?.message}" — retrying (#${attempt + 1}/${maxRetries}) in ${delay}ms`);
				await new Promise(r => setTimeout(r, delay));
				if (this.dead) throw e;
			}
		}
	}

	/** One prompt → its stream. Throws on a backend {t:'error'}. Caller (send) decides whether to retry. */
	private async *runAttempt(prompt: string, signal?: AbortSignal): AsyncIterable<StreamChunk> {
		// Wait for the prior turn (incl. a cancelled one, or a failed earlier attempt) to fully settle
		// before issuing the next prompt, so the helper services one run at a time and {t:'done'} can't
		// leak across turns.
		try { await this.settled; } catch { /* prior turn errored — fine, we proceed */ }
		if (this.dead) throw new Error('cursor session is dead');
		const chan = new Chan<StreamChunk>();
		this.active = chan;
		this.lastWasThinking = false;
		this.settled = new Promise<void>(r => { this.resolveSettled = r; });
		// Graceful interrupt: tell the helper to cancel the active run but KEEP the warm Agent alive for
		// the next turn (the helper streams in the background, so the cancel lands mid-run). The session
		// settles when the helper acks with {t:'done'}; the next send() waits on that.
		const onAbort = () => { try { this.child.stdin!.write(JSON.stringify({ t: 'cancel' }) + '\n'); } catch { /* stdin gone */ } };
		signal?.addEventListener('abort', onAbort, { once: true });
		this.child.stdin!.write(JSON.stringify({ t: 'prompt', prompt }) + '\n');
		try {
			yield* chan.iterate();
		} finally {
			signal?.removeEventListener('abort', onAbort);
		}
	}
}

/** Warm sessions keyed by the host-supplied `cursorSession` id. */
const SESSIONS = new Map<string, CursorSession>();

/**
 * Idle TTL: a warm helper holds a node subprocess + h2 connection open, so it can't live forever.
 * We don't evict on session-end (the host may resume a conversation later) — instead each helper is
 * reaped after `CURSOR_WARM_TTL_MS` of inactivity, re-spawned transparently on the next turn.
 * Default 10 min; set 0 to disable the reaper (helpers then live until disposeCursorSessions()).
 */
// `typeof process` guard: this Node-only adapter is in AIClient's eager import graph, so its module body
// must not touch Node globals at eval time (browser bundles have no `process`/`__dirname`).
const WARM_TTL_MS = typeof process !== 'undefined' && process.env.CURSOR_WARM_TTL_MS !== undefined
	? Number(process.env.CURSOR_WARM_TTL_MS)
	: 10 * 60_000;
const IDLE_TIMERS = new Map<string, ReturnType<typeof setTimeout>>();
function clearIdle(key: string): void {
	const t = IDLE_TIMERS.get(key);
	if (t) { clearTimeout(t); IDLE_TIMERS.delete(key); }
}
function armIdle(key: string, sess: CursorSession): void {
	clearIdle(key);
	if (!(WARM_TTL_MS > 0)) return; // 0 / NaN disables the reaper
	const t = setTimeout(() => {
		IDLE_TIMERS.delete(key);
		if (SESSIONS.get(key) === sess) SESSIONS.delete(key); // guard: don't evict a replacement under the same key
		sess.kill();
	}, WARM_TTL_MS);
	t.unref?.(); // the reaper must never be the thing keeping the event loop alive
	IDLE_TIMERS.set(key, t);
}

let exitHooked = false;
function hookExit(): void {
	if (exitHooked) return;
	exitHooked = true;
	// Belt: reap on natural exit. The host should ALSO call disposeCursorSessions() on teardown — a warm
	// child keeps the event loop alive (its stdout pipe is ref'd), so without an explicit dispose the
	// loop never drains and 'exit' never fires (the process would hang instead of exiting).
	process.on('exit', () => disposeCursorSessions());
}

/** Kill every warm cursor helper and clear the registry. Call from the host's REPL/process teardown. */
export function disposeCursorSessions(): void {
	for (const t of IDLE_TIMERS.values()) clearTimeout(t);
	IDLE_TIMERS.clear();
	for (const s of SESSIONS.values()) s.kill();
	SESSIONS.clear();
}

export class CursorAdapter extends BaseAdapter {
	get name(): string {
		return 'cursor';
	}

	async chat(options: ChatOptions): Promise<ChatResponse | AsyncIterable<StreamChunk>> {
		try {
			const apiKey = this.getApiKey(options);
			const modelName = options.model.replace(/^cursor\//, '');
			const prompt = buildPrompt(options.messages);
			const po = (options.providerOptions ?? {}) as Record<string, any>;
			const toolExecutor = po.toolExecutor as CursorToolExecutor | undefined;
			const toolDefs = toolExecutor ? toToolDefs(options.tools) : undefined;
			const req = {
				apiKey,
				modelName,
				model: toModelSelection(modelName),
				prompt,
				cwd: po.cwd || tmpdir(),
				mcpServers: po.mcpServers as Record<string, any> | undefined,
				toolDefs,
			};
			// Warm session (opt-in): reuse one long-lived agent across turns, keyed by `cursorSession`.
			const sessionKey = po.cursorSession ? `${po.cursorSession}:${req.modelName}` : undefined;
			if (options.stream && sessionKey) return this.warmStream(sessionKey, req, toolExecutor, options);
			return options.stream
				? this.stream(req, toolExecutor, options.signal)
				: await this.complete(req, toolExecutor, options.signal);
		} catch (error) {
			handleProviderError(error, this.name);
		}
	}

	private warmStream(key: string, req: Record<string, any>, toolExecutor: CursorToolExecutor | undefined, options: ChatOptions): AsyncIterable<StreamChunk> {
		hookExit();
		clearIdle(key); // turn starting → cancel any pending idle reap before (re)using the helper
		let sess = SESSIONS.get(key);
		if (!sess || sess.dead) { sess = new CursorSession(req, toolExecutor); SESSIONS.set(key, sess); }
		const live = sess;
		const inner = live.send(options.messages, options.signal);
		// Re-arm the idle TTL once this turn's stream is fully consumed (or aborts) — sequential turns
		// mean no overlap, so "stream done" is the right idle boundary.
		return (async function* () {
			try { yield* inner; } finally { armIdle(key, live); }
		})();
	}

	private async *stream(req: Record<string, any>, toolExecutor?: CursorToolExecutor, signal?: AbortSignal): AsyncIterable<StreamChunk> {
		const child = spawnHelper(toolExecutor);
		const onAbort = () => { try { child.kill('SIGTERM'); } catch { /* already gone */ } };
		signal?.addEventListener('abort', onAbort, { once: true });
		let spawnErr: Error | undefined;
		child.on('error', e => { spawnErr = e as Error; });
		child.stdin!.end(JSON.stringify({ ...req, stream: true }));
		try {
			let buf = '';
			outer: for await (const data of child.stdout!) {
				buf += (data as Buffer).toString();
				let nl: number;
				while ((nl = buf.indexOf('\n')) >= 0) {
					const line = buf.slice(0, nl).trim();
					buf = buf.slice(nl + 1);
					if (!line) continue;
					const msg = JSON.parse(line);
					if (msg.t === 'event') { for (const c of mapEvent(msg.e)) yield c; }
					else if (msg.t === 'done') break outer; // helper signaled completion — don't wait for stdout EOF (the IPC channel keeps the child alive)
					else if (msg.t === 'error' && !signal?.aborted) throw new Error(msg.message);
				}
				if (signal?.aborted) break;
			}
			if (spawnErr && !signal?.aborted) throw spawnErr;
			yield { content: '', finishReason: 'stop' };
		} finally {
			signal?.removeEventListener('abort', onAbort);
			try { child.kill('SIGTERM'); } catch { /* already gone */ }
		}
	}

	private complete(req: Record<string, any>, toolExecutor?: CursorToolExecutor, signal?: AbortSignal): Promise<ChatResponse> {
		return new Promise<ChatResponse>((resolve, reject) => {
			const child = spawnHelper(toolExecutor);
			const onAbort = () => { try { child.kill('SIGTERM'); } catch { /* already gone */ } };
			signal?.addEventListener('abort', onAbort, { once: true });
			let out = '';
			child.stdout!.on('data', d => (out += d.toString()));
			child.on('error', reject);
			child.on('close', () => {
				signal?.removeEventListener('abort', onAbort);
				const msgs = out.split('\n').map(l => { try { return JSON.parse(l); } catch { return null; } }).filter(Boolean) as any[];
				const err = msgs.find(m => m.t === 'error');
				if (err && !signal?.aborted) return reject(new Error(err.message));
				const result = msgs.find(m => m.t === 'result');
				resolve({ content: result?.result ?? '', finishReason: 'stop', model: req.modelName });
			});
			child.stdin!.end(JSON.stringify({ ...req, stream: false }));
		});
	}
}
